import { motion } from "framer-motion";
import { ArrowLeft, Shield, Lock, Database, Globe, UserCheck, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// Nephrix Logo Component
const NephrixIcon = ({ className = "h-8 w-8" }) => (
  <img 
    src="/nephrix-logo.png" 
    alt="Nephrix" 
    className={`${className} object-contain`}
  />
);

const SectionIcon = ({ icon: Icon, color = "blue" }) => (
  <div className={`w-10 h-10 rounded-xl bg-${color}-50 flex items-center justify-center mb-4`}>
    <Icon className={`w-5 h-5 text-${color}-600`} />
  </div>
);

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <NephrixIcon className="h-10 w-10" />
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors text-sm font-medium"
            data-testid="back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          {/* Title */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 mb-6">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h1 
              className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="privacy-title"
            >
              Privacy Policy
            </h1>
            <p className="text-slate-500 text-lg">Last updated: {currentDate}</p>
          </div>

          {/* Policy Content */}
          <div className="space-y-12">
            
            {/* Introduction */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Introduction
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Nephrix ("we", "our", or "us") operates the Nephrix mobile application and website (the "Service"). 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
                We are committed to protecting your privacy and handling your data with transparency and care.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                By using Nephrix, you agree to the collection and use of information in accordance with this policy. 
                If you do not agree with our policies and practices, please do not use our Service.
              </p>
            </section>

            {/* Medical Disclaimer */}
            <section className="bg-amber-50 rounded-2xl p-8 border border-amber-100">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Medical Disclaimer
                  </h2>
                  <p className="text-slate-700 leading-relaxed">
                    <strong>Important:</strong> Nephrix is designed as a health management and wellness tracking tool for dialysis patients. 
                    The Service does <strong>not</strong> provide medical diagnosis, treatment recommendations, or professional medical advice. 
                    Information provided by Nephrix, including AI-generated insights, should never replace consultation with qualified healthcare professionals.
                  </p>
                  <p className="text-slate-700 leading-relaxed mt-3">
                    Always consult your nephrologist, dialysis care team, or other qualified healthcare providers regarding any medical decisions or health concerns.
                    In case of a medical emergency, contact emergency services immediately.
                  </p>
                </div>
              </div>
            </section>

            {/* Information We Collect */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Database className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Information We Collect
                </h2>
              </div>
              
              <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">Personal Information</h3>
              <p className="text-slate-600 mb-3">When you create an account or use our Service, we may collect:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Name and email address</li>
                <li>Account credentials</li>
                <li>Country or region</li>
                <li>Dialysis type and treatment schedule (voluntarily provided)</li>
              </ul>

              <h3 className="text-lg font-medium text-slate-800 mt-8 mb-3">Health & Wellness Data</h3>
              <p className="text-slate-600 mb-3">
                To provide personalized insights, you may voluntarily enter:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Weight and fluid intake measurements</li>
                <li>Dialysis session information</li>
                <li>Lab values and health metrics</li>
                <li>Symptoms and wellness notes</li>
                <li>Nutrition and dietary information</li>
              </ul>

              <h3 className="text-lg font-medium text-slate-800 mt-8 mb-3">Usage Data</h3>
              <p className="text-slate-600 mb-3">We automatically collect certain information including:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Device type and operating system</li>
                <li>App usage patterns and feature interactions</li>
                <li>IP address and general location</li>
                <li>Crash reports and performance data</li>
              </ul>
            </section>

            {/* AI Interaction Data */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                AI Interaction Data
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Nephrix uses artificial intelligence to provide personalized insights and respond to your questions about kidney health and dialysis management. 
                When you interact with our AI features:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Your questions and inputs may be processed to generate responses</li>
                <li>Interactions may be analyzed to improve AI accuracy and relevance</li>
                <li>AI responses are generated based on general kidney health information, not your specific medical records</li>
                <li>We do not share your AI conversations with third parties for marketing purposes</li>
              </ul>
            </section>

            {/* How We Use Information */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                How We Use Your Information
              </h2>
              <p className="text-slate-600 mb-4">We use collected information to:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Provide, maintain, and improve the Nephrix Service</li>
                <li>Personalize your experience and deliver relevant insights</li>
                <li>Process and track your health and wellness data</li>
                <li>Send important notifications about your account or the Service</li>
                <li>Analyze usage patterns to enhance features and user experience</li>
                <li>Ensure the security and integrity of the platform</li>
                <li>Comply with legal obligations</li>
              </ul>
              <p className="text-slate-600 mt-6 font-medium">
                We do not sell your personal data to third parties.
              </p>
            </section>

            {/* Third-Party Services */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Third-Party Services
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We may use third-party services to help operate and improve Nephrix, including:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li><strong>Cloud hosting providers</strong> for secure data storage</li>
                <li><strong>Analytics services</strong> to understand app usage</li>
                <li><strong>AI service providers</strong> to power intelligent features</li>
                <li><strong>Communication services</strong> for notifications and support</li>
              </ul>
              <p className="text-slate-600 mt-4">
                These providers are contractually obligated to protect your data and use it only for specified purposes.
              </p>
            </section>

            {/* GDPR Compliance */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  GDPR Compliance
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, you have certain rights under the General Data Protection Regulation (GDPR).
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                Our legal bases for processing your data include:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-3">
                <li><strong>Consent:</strong> When you explicitly agree to data processing</li>
                <li><strong>Contract:</strong> When processing is necessary to provide the Service</li>
                <li><strong>Legitimate Interest:</strong> To improve and secure our Service</li>
                <li><strong>Legal Obligation:</strong> When required by applicable law</li>
              </ul>
            </section>

            {/* Data Retention */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Data Retention
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, 
                unless a longer retention period is required by law.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                When you delete your account, we will delete or anonymize your personal data within 30 days, 
                except where we are required to retain certain information for legal, accounting, or regulatory purposes.
              </p>
            </section>

            {/* Security */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Security
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                We implement industry-standard security measures to protect your personal information, including:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication mechanisms</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls limiting data access to authorized personnel</li>
              </ul>
              <p className="text-slate-600 mt-4">
                However, no method of transmission over the Internet is 100% secure. While we strive to protect your data, 
                we cannot guarantee absolute security.
              </p>
            </section>

            {/* User Rights */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-teal-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Your Rights
                </h2>
              </div>
              <p className="text-slate-600 mb-4">Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Restriction:</strong> Limit how we process your data</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Objection:</strong> Object to certain data processing activities</li>
                <li><strong>Withdraw Consent:</strong> Withdraw previously given consent at any time</li>
              </ul>
              <p className="text-slate-600 mt-4">
                To exercise these rights, please contact us at the email address below.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Children's Privacy
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Nephrix is not intended for individuals under the age of 18. We do not knowingly collect personal information from children.
                If you are a parent or guardian and believe your child has provided us with personal data, please contact us immediately.
                We will take steps to delete such information from our systems.
              </p>
            </section>

            {/* International Transfers */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                International Data Transfers
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Your information may be transferred to and processed in countries other than your own. 
                These countries may have different data protection laws. When we transfer data internationally, 
                we ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable laws.
              </p>
            </section>

            {/* Changes to Policy */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Changes to This Privacy Policy
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons.
                We will notify you of any material changes by updating the "Last updated" date and, where appropriate, providing additional notice within the app or via email.
              </p>
            </section>

            {/* Contact Information */}
            <section className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 border border-blue-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Contact Information
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-white rounded-xl p-6 border border-slate-100">
                <p className="text-slate-700">
                  <strong>Privacy Inquiries:</strong>{" "}
                  <a href="mailto:privacy@nephrixapp.com" className="text-blue-600 hover:underline">
                    privacy@nephrixapp.com
                  </a>
                </p>
                <p className="text-slate-700 mt-2">
                  <strong>General Support:</strong>{" "}
                  <a href="mailto:support@nephrixapp.com" className="text-blue-600 hover:underline">
                    support@nephrixapp.com
                  </a>
                </p>
                <p className="text-slate-700 mt-2">
                  <strong>Website:</strong>{" "}
                  <a href="https://www.nephrixapp.com" className="text-blue-600 hover:underline">
                    www.nephrixapp.com
                  </a>
                </p>
              </div>
            </section>
          </div>

          {/* Back to Home */}
          <div className="mt-16 text-center">
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition-all hover:-translate-y-0.5 shadow-lg shadow-blue-500/25"
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-8 mt-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center">
              <NephrixIcon className="h-10 w-10" />
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-blue-600 transition-colors">Terms of Service</Link>
              <a href="mailto:support@nephrixapp.com" className="hover:text-blue-600 transition-colors">support@nephrixapp.com</a>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-400">
              Nephrix does not provide medical diagnosis and does not replace professional medical advice.
            </p>
            <p className="text-xs text-slate-400 mt-2">
              © {new Date().getFullYear()} Nephrix. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
